import React, { useState, useEffect, useRef } from 'react';
import { Table, Tabs, Select } from 'antd';
import { getDecisionLogData, getModes } from './getDecisionLogData';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import NoTableDataSVG from './NoTableDataSVG';
import { DateTimePicker } from '../../components/NWL/Common/datetimepicker';
import type { TabsProps } from 'antd';
import {
	getSimulationTimeline,
	downloadSimulationTimeline,
	getStrategies,
} from '../../services/digitalTwins';
import { getLocations } from '../../services/getLocations';

import './styles.scss';

// Introducing types for decision log rows
type RowType = 'IMPLEMENT' | 'SMART_EVENT' | 'RECOMMEND';

type LogData = {
	key: string | number;
	rowType: RowType;
	timestamp?: number;
	displayTime: string;
	locationId?: number;
	location?: string;
	mode?: string;

	// SMART_EVENT row fields
	smartEventText?: string;
	smartEventOn?: boolean;
};

type DropdownItem = { label: string; key: string };
type LocationOption = { label: string; value: string };

const normaliseLocId = (raw: any): number | undefined => {
	const n = Number(raw);
	return Number.isFinite(n) ? n : undefined;
};

const pickLocId = (r: any, locationId?: number): number | undefined =>
	normaliseLocId(r?.locationId ?? r?.locId ?? locationId);

const NoRecords = () => (
	<div className="decision-log-no-records">
		<NoTableDataSVG />
		<h2>No strategy decisions recorded</h2>
		<span>There are no strategy implementation decisions recorded</span>
	</div>
);

const parseCsvSimple = (csvText: string) => {
	const lines = csvText.trim().split(/\r?\n/);
	if (lines.length < 2) return { headers: [], rows: [] as Record<string, string>[] };

	const headers = lines[0].split(',').map((h) => h.trim());
	const rows = lines
		.slice(1)
		.filter(Boolean)
		.map((line) => {
			const values = line.split(',');
			const row: Record<string, string> = {};
			headers.forEach((h, i) => (row[h] = (values[i] ?? '').trim()));
			return row;
		});

	return { headers, rows };
};

const toCsv = (headers: string[], rows: Record<string, any>[]) => {
	const escape = (v: any) => {
		const s = v == null ? '' : String(v);
		if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
		return s;
	};

	const headerLine = headers.map(escape).join(',');
	const body = rows.map((r) => headers.map((h) => escape(r[h])).join(',')).join('\n');
	return `${headerLine}\n${body}\n`;
};

const downloadCsvText = (csvText: string, filename: string) => {
	if (!csvText || !csvText.trim()) return;
	const blob = new Blob(['\uFEFF' + csvText], { type: 'text/csv;charset=utf-8;' });
	const url = window.URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();

	window.URL.revokeObjectURL(url);
};

const modifyRecommendedCsv = (csvText: string) => {
	const { rows } = parseCsvSimple(csvText);
	if (!rows.length) return csvText;

	const modify = rows
		.map((r) => {
			const iso = r.recommendationTime;
			const dateTime = iso ? dayjs(iso).format('YYYY/MM/DD [at] HH:mm') : '';

			const strategy = (r.recommendedStrategy ?? '').trim();

			return {
				'Date & time recommended': dateTime,
				Location: (r.locationName ?? '').trim(),
				'Recommended strategy': strategy,
			};
		})
		.filter((r) => r.Location || r['Recommended strategy'] || r['Date & time recommended'])
		.sort(
			(a, b) =>
				(dayjs(b['Date & time recommended']).valueOf() || 0) -
				(dayjs(a['Date & time recommended']).valueOf() || 0),
		);
	const headers = ['Date & time recommended', 'Location', 'Recommended strategy'];
	return toCsv(headers, modify);
};

const TableView = ({
	tableData,
	onDateRangeChange,
	dropdownItems,
	selectedMode,
	setSelectedMode,
	totalRecords,
	setPageNumber,
	dateRange,
	dateColumnTitle,
	strategyColumnTitle,
	locationOptions,
	selectedLocations,
	onLocationsChange,
	locationSummary,
	showExport,
	onExport,
}: {
	tableData: LogData[] | null;
	onDateRangeChange: (dates?: [Dayjs, Dayjs]) => void;
	dropdownItems: DropdownItem[];
	selectedMode: string;
	setSelectedMode: (v: string) => void;
	totalRecords: number;
	setPageNumber: (page: number) => void;
	dateRange: [Dayjs, Dayjs] | undefined;
	dateColumnTitle: string;
	strategyColumnTitle: string;
	locationOptions: LocationOption[];
	selectedLocations: string[];
	onLocationsChange: (values: string[]) => void;
	locationSummary: string;
	showExport: boolean;
	onExport: () => void;
}) => {
	return (
		<>
			<div className="decision-log-filters">
				<DateTimePicker
					data-testid="datetime-picker"
					id="datetime-picker"
					value={dateRange ?? undefined}
					allowClear={false}
					onCancel={() => {}}
					onSave={(dates: [Dayjs, Dayjs] | null) => {
						onDateRangeChange(dates ?? undefined);
					}}
				/>
				<Select
					data-testid="location-selector"
					id="location-selector"
					className="dl-multiselect-select"
					mode="multiple"
					value={selectedLocations}
					options={locationOptions}
					onChange={onLocationsChange}
					showSearch={false}
					menuItemSelectedIcon={null}
					maxTagCount={0}
					maxTagPlaceholder={() => (
						<span className="dl-multiselect-summary">{locationSummary}</span>
					)}
					optionRender={(option) => {
						const value = String((option as any).value);
						const label = String((option as any).label);

						const totalLocations = locationOptions.filter((o) => o.value !== 'all').length;
						const selectedInd = selectedLocations.filter((v) => v !== 'all');

						const allSelected =
							selectedLocations.includes('all') ||
							(totalLocations > 0 && selectedInd.length === totalLocations);

						const isAllRow = value === 'all';
						const isChecked = isAllRow ? allSelected : allSelected || selectedInd.includes(value);
						const isIndeterminate = isAllRow && !allSelected && selectedInd.length > 0;

						return (
							<div className="dl-option">
								<span
									className={[
										'dl-checkbox',
										isChecked ? 'is-checked' : '',
										isIndeterminate ? 'is-indeterminate' : '',
									].join(' ')}
								>
									{isChecked ? <CheckOutlined /> : isIndeterminate ? <MinusOutlined /> : null}
								</span>

								<span className="dl-option-label">{label}</span>
							</div>
						);
					}}
					style={{ minWidth: 200 }}
				/>

				<Select
					data-testid="strategy-selector"
					id="strategy-selector"
					className="dl-multiselect-select"
					style={{ minWidth: 200, marginRight: 12 }}
					value={selectedMode}
					onChange={setSelectedMode}
					options={dropdownItems.map((i) => ({
						value: i.key,
						label: i.label,
					}))}
					showSearch
					optionFilterProp="label"
				/>
				{showExport ? (
					<button
						data-testid="export-button"
						id="export-button"
						onClick={onExport}
						className="custom-outline-btn"
						style={{ justifyContent: 'right', marginLeft: 'auto' }}
					>
						<svg
							width="21"
							height="20"
							viewBox="0 0 21 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className="size-4 mr-0.5"
						>
							<path
								d="M18.0001 10C17.7791 10 17.5671 10.0878 17.4108 10.2441C17.2545 10.4004 17.1667 10.6123 17.1667 10.8333V15.8333C17.1667 16.0543 17.079 16.2663 16.9227 16.4226C16.7664 16.5789 16.5544 16.6667 16.3334 16.6667H4.66675C4.44573 16.6667 4.23377 16.5789 4.07749 16.4226C3.92121 16.2663 3.83341 16.0543 3.83341 15.8333V10.8333C3.83341 10.6123 3.74562 10.4004 3.58934 10.2441C3.43306 10.0878 3.2211 10 3.00008 10C2.77907 10 2.56711 10.0878 2.41083 10.2441C2.25455 10.4004 2.16675 10.6123 2.16675 10.8333V15.8333C2.16675 16.4964 2.43014 17.1323 2.89898 17.6011C3.36782 18.0699 4.00371 18.3333 4.66675 18.3333H16.3334C16.9965 18.3333 17.6323 18.0699 18.1012 17.6011C18.57 17.1323 18.8334 16.4964 18.8334 15.8333V10.8333C18.8334 10.6123 18.7456 10.4004 18.5893 10.2441C18.4331 10.0878 18.2211 10 18.0001 10Z"
								fill="#535662"
							/>
							<path
								d="M11.3346 10.4959L13.243 8.5792C13.3207 8.5015 13.4129 8.43986 13.5144 8.39781C13.616 8.35576 13.7248 8.33412 13.8346 8.33412C13.9445 8.33412 14.0533 8.35576 14.1549 8.39781C14.2564 8.43986 14.3486 8.5015 14.4263 8.5792C14.504 8.65689 14.5656 8.74914 14.6077 8.85066C14.6497 8.95217 14.6714 9.06098 14.6714 9.17086C14.6714 9.28075 14.6497 9.38955 14.6077 9.49107C14.5656 9.59259 14.504 9.68483 14.4263 9.76253L11.093 13.0959C11.0137 13.1717 10.9203 13.2312 10.818 13.2709C10.6151 13.3542 10.3875 13.3542 10.1846 13.2709C10.0824 13.2312 9.9889 13.1717 9.90964 13.0959L6.57631 9.76253C6.4982 9.68506 6.43621 9.59289 6.3939 9.49134C6.35159 9.38979 6.32981 9.28087 6.32981 9.17086C6.32981 9.06085 6.35159 8.95193 6.3939 8.85038C6.43621 8.74883 6.4982 8.65666 6.57631 8.5792C6.65378 8.50109 6.74595 8.43909 6.8475 8.39679C6.94905 8.35448 7.05797 8.3327 7.16798 8.3327C7.27799 8.3327 7.38691 8.35448 7.48846 8.39679C7.59001 8.43909 7.68217 8.50109 7.75964 8.5792L9.66798 10.4959L9.66798 2.5042C9.66798 2.28318 9.75578 2.07122 9.91206 1.91494C10.0683 1.75866 10.2803 1.67086 10.5013 1.67086C10.7223 1.67086 10.9343 1.75866 11.0906 1.91494C11.2468 2.07122 11.3346 2.28318 11.3346 2.5042L11.3346 10.4959Z"
								fill="#535662"
							/>
						</svg>
						<span className="ml-1">Export</span>
					</button>
				) : null}
			</div>

			<Table<LogData>
				dataSource={tableData || []}
				onRow={(record) => ({
					'data-testid': 'list-item',
					'data-row-type': record.rowType,
					'data-timestamp': record.timestamp ?? '',
					id: `list-item-${record.key}`,
				})}
				rowClassName={(record) => (record.rowType === 'SMART_EVENT' ? 'smart-network-row' : '')}
				columns={[
					{
						title: (
							<span id="datetime-header" data-testid="datetime-header">
								{dateColumnTitle}
							</span>
						),
						dataIndex: 'displayTime',
						width: '35%',
						onCell: (record) => ({
							id: `datetime-${record.key}`,
							'data-testid': 'datetime',
						}),
						render: (_text, record) => {
							if (record.rowType === 'SMART_EVENT') {
								return {
									children: (
										<div className="smart-event" id={`smart-event-${record.key}`}>
											<span className={record.smartEventOn ? 'icon on' : 'icon off'}>
												<svg
													width="16"
													height="16"
													viewBox="0 0 24 25"
													fill="currentColor"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path d="M15.1045 4.49005C15.3351 4.02654 15.8773 3.82017 16.3516 3.99884L16.4453 4.03986L16.75 4.19806C19.8744 5.88751 21.9999 9.19463 22 12.9998C22 18.5227 17.5228 22.9998 12 22.9998C6.47715 22.9998 2 18.5227 2 12.9998C2.00007 9.07216 4.26458 5.67501 7.55469 4.03986C8.04914 3.79417 8.64963 3.99574 8.89551 4.49005C9.14125 4.98454 8.93968 5.585 8.44531 5.83087C5.80877 7.14112 4.00007 9.86027 4 12.9998C4 17.4181 7.58172 20.9998 12 20.9998C16.4183 20.9998 20 17.4181 20 12.9998C19.9999 9.86027 18.1912 7.14112 15.5547 5.83087C15.0603 5.585 14.8588 4.98454 15.1045 4.49005ZM12 0.999817C12.2652 0.999817 12.5195 1.11585 12.707 1.32208C12.8946 1.52837 13 1.80866 13 2.1004V10.9002C13 11.1918 12.8945 11.4713 12.707 11.6776C12.5195 11.8838 12.2652 11.9998 12 11.9998C11.7348 11.9998 11.4805 11.8838 11.293 11.6776C11.1055 11.4713 11 11.1918 11 10.9002V2.1004C11 1.80866 11.1054 1.52837 11.293 1.32208C11.4805 1.11585 11.7348 0.999817 12 0.999817Z" />
												</svg>
											</span>
											<span>{record.smartEventText}</span>
										</div>
									),
									props: { colSpan: 3 },
								};
							}
							return record.displayTime;
						},
					},
					{
						title: (
							<span id="location-header" data-testid="location-header">
								Location
							</span>
						),
						dataIndex: 'location',
						width: '30%',
						onCell: (record) => ({
							id: `location-${record.key}`,
							'data-testid': 'location',
						}),
						render: (_text, record) => {
							if (record.rowType === 'SMART_EVENT') {
								return { props: { colSpan: 0 } };
							}
							return record.location;
						},
					},
					{
						title: (
							<span id="strategy-header" data-testid="strategy-header">
								{strategyColumnTitle}
							</span>
						),
						dataIndex: 'mode',
						width: '35%',
						onCell: (record) => ({
							id: `strategy-${record.key}`,
							'data-testid': 'strategy',
						}),
						render: (_text, record) => {
							if (record.rowType === 'SMART_EVENT') {
								return { props: { colSpan: 0 } };
							}
							return record.mode;
						},
					},
				]}
				pagination={{
					position: ['bottomLeft'],
					defaultPageSize: 10,
					hideOnSinglePage: false,
					pageSizeOptions: [10, 20, 50],
					showSizeChanger: false,
					total: totalRecords,
					onChange: (page) => setPageNumber(page),
				}}
			/>
		</>
	);
};

const formatTs = (tsSec: number) => {
	const m = dayjs.unix(tsSec);
	return m.isValid() ? m.format('YYYY/MM/DD [at] HH:mm') : '—';
};

const normaliseTsSec = (rawTs: any) => {
	const n = Number(rawTs);
	if (!Number.isFinite(n)) return undefined;
	return n > 1e12 ? Math.floor(n / 1000) : n;
};

const pickLocationLabel = (r: any, fallback?: string) =>
	String(
		r?.catchment ?? r?.location ?? r?.locationName ?? r?.catchmentName ?? fallback ?? '',
	).trim();

const parseImplementedData = (
	api: any,
	resolveLocationName: (id: number) => string,
	fallbackLabel: string,
	locationId?: number,
) => {
	type Group = {
		tsSec: number;
		levelDesc?: string;
		modeVal?: 0 | 1;
		locationId?: number;
		locationLabel: string;
	};
	const groups: Record<string, Group> = {};

	(api?.data ?? []).forEach((r: any) => {
		const tsSec = normaliseTsSec(r.timestamp ?? r.implemented);
		if (!tsSec) return;

		const locId = pickLocId(r, locationId);
		const locationLabel =
			locId != null
				? resolveLocationName(locId)
				: pickLocationLabel(r, fallbackLabel) || fallbackLabel;
		const groupKey = `${locId ?? locationLabel}-${tsSec}`;

		if (!groups[groupKey]) groups[groupKey] = { tsSec, locationId: locId, locationLabel };

		if (r.type === 'SMART_LEVEL_CONTROL') {
			const trimmed = r.mode == null ? '' : String(r.mode).trim();
			if (trimmed) groups[groupKey].levelDesc = trimmed;
		}

		if (r.type === 'SMART_CONTROL_MODE') {
			const v = typeof r.value === 'number' ? r.value : Number(r.value);
			if (v === 0 || v === 1) groups[groupKey].modeVal = v as 0 | 1;
		}
	});

	const rows: LogData[] = [];

	Object.values(groups)
		.sort((a, b) => b.tsSec - a.tsSec)
		.forEach((g, idx) => {
			const displayDatetime = formatTs(g.tsSec);
			const modeLabel = (g.levelDesc ?? '').trim();

			if (modeLabel) {
				rows.push({
					key: `impl-${g.locationLabel}-${g.tsSec}-${idx}`,
					rowType: 'IMPLEMENT',
					timestamp: g.tsSec,
					displayTime: displayDatetime,
					locationId: g.locationId,
					location: g.locationLabel,
					mode: modeLabel,
				});
			}

			if (g.modeVal !== undefined) {
				const on = g.modeVal === 1;
				rows.push({
					key: `evt-${g.locationId ?? g.locationLabel}-${g.tsSec}-${idx}`,
					rowType: 'SMART_EVENT',
					timestamp: g.tsSec,
					displayTime: '',
					locationId: g.locationId,
					smartEventOn: on,
					smartEventText: `Smart network turned ${on ? 'on' : 'off'}, ${displayDatetime}`,
				});
			}
		});

	return { logData: rows };
};

const parseRecommendedData = (api: any, fallbackLabel: string) => {
	const rows: LogData[] = Array.isArray(api?.data)
		? api.data
				.map((r: any, idx: number) => {
					const tsSec = normaliseTsSec(r?.recommendationTime);
					if (!tsSec) return null;

					const strategy = String(r?.recommendedStrategy ?? '').trim();
					if (!strategy) return null;

					const locationLabel = String(r?.locationName ?? fallbackLabel).trim() || fallbackLabel;

					return {
						key: `rec-${locationLabel}-${tsSec}-${idx}`,
						rowType: 'RECOMMEND' as const,
						timestamp: tsSec,
						displayTime: formatTs(tsSec),
						location: locationLabel,
						mode: strategy,
					};
				})
				.filter(Boolean)
		: [];

	rows.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

	return { logData: rows };
};

type DecisionTabKey = 'implemented' | 'recommended';

const DecisionLogView = () => {
	const security = JSON.parse(localStorage.getItem('security') || '{}');
	const organizationId = security.organizationId;

	const [decisionLogData, setDecisionLogData] = useState<LogData[] | null>(null);
	const [tableData, setTableData] = useState<LogData[] | null>(null);
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined);
	const [dropdownItems, setDropdownItems] = useState<DropdownItem[]>([
		{ label: 'Any strategy', key: '0' },
	]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [activeTab, setActiveTab] = useState<DecisionTabKey>('implemented');
	const [selectedLocations, setSelectedLocations] = useState<string[]>(['all']);
	const [locations, setLocations] = useState([] as any[]);
	const [selectedMode, setSelectedMode] = useState<string>('0');
	const optionsCacheRef = useRef<Record<string, string[]>>({});

	useEffect(() => {
		if (!organizationId) return;

		let cancelled = false;

		(async () => {
			try {
				const resp = await getLocations(organizationId);
				if (cancelled) return;

				const list = Array.isArray(resp) ? resp : [];
				setLocations(list);
			} catch {
				if (cancelled) return;
				setLocations([]);
			} finally {
				if (cancelled) return;
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [organizationId]);

	const locationOptions: LocationOption[] = React.useMemo(() => {
		const opts: LocationOption[] = [
			{ label: 'All locations', value: 'all' },
			...locations.map((l) => ({
				label: l.name,
				value: String(l.id),
			})),
		];

		const seen = new Set<string>();
		return opts.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
	}, [locations]);

	useEffect(() => {
		if (selectedLocations.includes('all')) return;

		const allowed = new Set(locations.map((l) => String(l.id)));
		const filtered = selectedLocations.filter((v) => allowed.has(v));

		if (filtered.length !== selectedLocations.length) {
			setSelectedLocations(filtered.length ? filtered : ['all']);
		}
	}, [locations]);

	const modesCacheRef = useRef<Record<string, string[]>>({});

	type SelectedLocId = number | 'all';
	const selectedLocIds = React.useMemo<SelectedLocId[]>(() => {
		if (selectedLocations.includes('all')) return ['all'];

		return selectedLocations.map((v) => Number(v)).filter(Number.isFinite);
	}, [selectedLocations]);

	const selectedLocIdsKey = React.useMemo(
		() => selectedLocIds.map(String).join(','),
		[selectedLocIds],
	);

	const onLocationsChange = (values: string[]) => {
		setSelectedLocations((prev) => {
			const next = Array.isArray(values) ? values : [];

			if (next.length === 0) return ['all'];

			if (prev.includes('all') && next.includes('all') && next.length > 1) {
				return next.filter((v) => v !== 'all');
			}
			if (next.includes('all')) return ['all'];

			return next;
		});
	};

	useEffect(() => {
		setPageNumber(1);
	}, [dateRange, selectedMode, activeTab, selectedLocations]);

	const locationNameById = React.useMemo(() => {
		const m = new Map<number, string>();
		(locations ?? []).forEach((l: any) => {
			const id = Number(l.id);
			if (Number.isFinite(id)) m.set(id, String(l.name ?? `Location ${id}`));
		});
		return m;
	}, [locations]);

	const resolveLocationName = React.useCallback(
		(locId: number) => locationNameById.get(locId) ?? `Location ${locId}`,
		[locationNameById],
	);

	const locationSummary = React.useMemo(() => {
		const total = locations.length;

		if (selectedLocations.includes('all')) return 'All locations';

		if (selectedLocIds.length === 0) return 'All locations';

		if (total > 0 && selectedLocIds.length === total) return 'All locations';

		const firstName = resolveLocationName(selectedLocIds[0] as any);

		if (selectedLocIds.length === 1) return firstName;

		return `${firstName}, +${selectedLocIds.length - 1}`;
	}, [selectedLocations, selectedLocIds, locations, resolveLocationName]);

	useEffect(() => {
		if (!organizationId) return;
		if (!selectedLocIdsKey) return;

		let cancelled = false;

		const extractList = (resp: any, locKey: string) => {
			if (Array.isArray(resp)) {
				if (resp.every((x) => typeof x === 'string')) return resp;

				const entry = resp.find((x: any) => String(x?.locationId) === locKey) ?? resp[0];

				if (Array.isArray(entry?.modes)) return entry.modes;
				if (Array.isArray(entry?.strategies)) return entry.strategies;
			}

			if (Array.isArray(resp?.modes)) return resp.modes;
			if (Array.isArray(resp?.strategies)) return resp.strategies;

			return [];
		};

		const fetchOptionsForLoc = async (locId: any) => {
			return activeTab === 'recommended'
				? await getStrategies(organizationId, locId)
				: await getModes(locId, organizationId);
		};

		(async () => {
			try {
				// ALL LOCATIONS
				if (selectedLocIds.length === 1 && selectedLocIds[0] === 'all') {
					const locKey = 'all';
					const cacheKey = `${activeTab}:${locKey}`;

					if (!optionsCacheRef.current[cacheKey]) {
						const r = await fetchOptionsForLoc('all' as any);
						if (cancelled) return;

						optionsCacheRef.current[cacheKey] = extractList(r, locKey)
							.map((x: any) => String(x).trim())
							.filter(Boolean);
					}

					const unique = Array.from(new Set(optionsCacheRef.current[cacheKey]));

					const items: DropdownItem[] = [
						{ label: 'Any strategy', key: '0' },
						...unique.map((label) => ({ key: label, label })),
					];

					setDropdownItems(items);
					setSelectedMode((prev) => (items.some((i) => i.key === prev) ? prev : '0'));
					return;
				}

				// SPECIFIC LOCATIONS
				const ids = selectedLocIds.filter((x): x is number => x !== 'all');

				const toFetch = ids.filter((id) => {
					const cacheKey = `${activeTab}:${String(id)}`;
					return !optionsCacheRef.current[cacheKey];
				});

				if (toFetch.length) {
					const results = await Promise.all(
						toFetch.map(async (locId) => {
							try {
								const r = await fetchOptionsForLoc(locId as any);
								return { locId, r };
							} catch {
								return { locId, r: null };
							}
						}),
					);

					if (cancelled) return;

					results.forEach(({ locId, r }) => {
						const cacheKey = `${activeTab}:${String(locId)}`;
						optionsCacheRef.current[cacheKey] = extractList(r, String(locId))
							.map((x: any) => String(x).trim())
							.filter(Boolean);
					});
				}

				const union = ids
					.flatMap((id) => optionsCacheRef.current[`${activeTab}:${String(id)}`] ?? [])
					.map((x) => String(x).trim())
					.filter(Boolean);

				const unique = Array.from(new Set(union));

				const items: DropdownItem[] = [
					{ label: 'Any strategy', key: '0' },
					...unique.map((label) => ({ key: label, label })),
				];

				setDropdownItems(items);
				setSelectedMode((prev) => (items.some((i) => i.key === prev) ? prev : '0'));
			} catch {
				if (cancelled) return;
				const fallback = { label: 'Any strategy', key: '0' };
				setDropdownItems([fallback]);
				setSelectedMode('0');
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [organizationId, selectedLocIdsKey, activeTab]);

	useEffect(() => {
		setSelectedMode('0');
	}, [activeTab]);

	useEffect(() => {
		const fetchData = async () => {
			if (!organizationId) return;
			if (!selectedLocIds.length) return;

			const startTs = dateRange?.[0]?.isValid() ? dateRange[0].unix() : undefined;
			const endTs = dateRange?.[1]?.isValid() ? dateRange[1].unix() : undefined;
			const modeFilter = selectedMode === '0' ? undefined : selectedMode;

			if (activeTab === 'implemented') {
				if (selectedLocIds.length === 1 && selectedLocIds[0] === 'all') {
					const resp = getDecisionLogData({
						orgId: organizationId,
						locId: 'all' as any,
						startDate: startTs,
						endDate: endTs,
						mode: modeFilter,
						page: pageNumber,
					});

					const d = await resp.historic;

					const parsed = parseImplementedData(d, resolveLocationName, 'All locations').logData;

					setDecisionLogData(parsed);
					setTableData(parsed);
					setTotalRecords(d?.total ?? parsed.length);
					return;
				}

				const ids = selectedLocIds.filter((x): x is number => x !== 'all');

				const results = await Promise.all(
					ids.map(async (locId) => {
						const resp = getDecisionLogData({
							orgId: organizationId,
							locId: locId as any,
							startDate: startTs,
							endDate: endTs,
							mode: modeFilter,
							page: pageNumber,
						});

						const d = await resp.historic;
						const locName = resolveLocationName(locId);

						return {
							total: typeof d?.total === 'number' ? d.total : undefined,
							rows: parseImplementedData(d, resolveLocationName, locName, locId).logData,
						};
					}),
				);

				const merged = results
					.flatMap((r) => r.rows)
					.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

				const maxTotal = results.reduce(
					(acc, r) => {
						const t = typeof r.total === 'number' ? r.total : undefined;
						if (t == null) return acc;
						return acc == null ? t : Math.max(acc, t);
					},
					undefined as number | undefined,
				);

				setDecisionLogData(merged);
				setTableData(merged);
				setTotalRecords(maxTotal ?? merged.length);
				return;
			}

			// RECOMMENDED
			if (selectedLocIds.length === 1 && selectedLocIds[0] === 'all') {
				const resp = getSimulationTimeline({
					orgId: organizationId,
					locId: 'all',
					dtId: 'all',
					page: pageNumber,
					pageSize: 10,
					startDate: startTs,
					endDate: endTs,
					mode: modeFilter,
				});

				const d = await resp.timeline;
				let rows = parseRecommendedData(d, 'All locations').logData;

				setDecisionLogData(rows);
				setTableData(rows);
				setTotalRecords(d?.total);
				return;
			}

			const ids = selectedLocIds.filter((x): x is number => x !== 'all');

			if (!ids.length) {
				setDecisionLogData([]);
				setTableData([]);
				setTotalRecords(0);
				return;
			}

			const resp = getSimulationTimeline({
				orgId: organizationId,
				locId: 'all',
				dtId: 'all',
				page: pageNumber,
				pageSize: 10,
				startDate: startTs,
				endDate: endTs,
				mode: modeFilter,
				locIds: ids.join(','),
			});

			const d = await resp.timeline;
			let rows = parseRecommendedData(d, 'All locations').logData;

			setDecisionLogData(rows);
			setTableData(rows);
			setTotalRecords(d?.total);
		};

		fetchData();
	}, [
		organizationId,
		activeTab,
		selectedLocIds,
		dateRange,
		selectedMode,
		pageNumber,
		locationNameById,
	]);

	const onExport = async () => {
		if (!organizationId) return;
		if (activeTab !== 'recommended') return;

		const isAll = selectedLocIds.length === 1 && selectedLocIds[0] === 'all';
		const ids = selectedLocIds.filter((x): x is number => x !== 'all');

		const startTs = dateRange?.[0]?.isValid() ? dateRange[0].unix() : undefined;
		const endTs = dateRange?.[1]?.isValid() ? dateRange[1].unix() : undefined;
		const modeFilter = selectedMode === '0' ? undefined : selectedMode;

		try {
			if (isAll) {
				const { timeline } = await downloadSimulationTimeline({
					orgId: organizationId,
					locId: 'all',
					dtId: 'all',
					startDate: startTs,
					endDate: endTs,
					mode: modeFilter,
					limit: totalRecords || 100000,
				});
				const csvText = await timeline;
				const modified = modifyRecommendedCsv(csvText);

				downloadCsvText(
					modified,
					`Recommended_Strategies_(Decision_Log)_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`,
				);
				return;
			}

			if (!ids.length) return;
			if (!tableData || tableData.length === 0) return;

			const { timeline } = await downloadSimulationTimeline({
				orgId: organizationId,
				locId: 'all',
				dtId: 'all',
				startDate: startTs,
				endDate: endTs,
				mode: modeFilter,
				locIds: ids.join(','),
				limit: totalRecords || 100000,
			});
			const csvText = await timeline;
			const modified = modifyRecommendedCsv(csvText);

			downloadCsvText(
				modified,
				`Recommended_Strategies_(Decision_Log)_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`,
			);
		} catch (e) {
			console.error('Export failed', e);
		}
	};

	const hasExportData =
		activeTab === 'recommended' && Array.isArray(tableData) && tableData.length > 0;

	const tabItems: TabsProps['items'] = [
		{
			key: 'implemented',
			label: (
				<span data-testid="implemented-strategy-tab" id="implemented-strategy-tab">
					Implemented
				</span>
			),
		},
		{
			key: 'recommended',
			label: (
				<span data-testid="recommended-strategy-tab" id="recommended-strategy-tab">
					Recommended
				</span>
			),
		},
	];
	return (
		<div className="decision-log">
			<h1>Decision log</h1>
			<span className="text-[16px]">
				Record of implemented strategies based on recommendations.
			</span>
			<Tabs
				id="item"
				data-testid="item"
				className="decision-log-tabs"
				activeKey={activeTab}
				items={tabItems}
				onChange={(key) => setActiveTab(key as DecisionTabKey)}
			/>
			{decisionLogData ? (
				<TableView
					tableData={tableData ?? []}
					onDateRangeChange={setDateRange}
					dropdownItems={dropdownItems}
					selectedMode={selectedMode}
					setSelectedMode={setSelectedMode}
					totalRecords={totalRecords}
					setPageNumber={setPageNumber}
					dateRange={dateRange}
					dateColumnTitle={
						activeTab === 'implemented' ? 'Date and time implemented' : 'Date and time recommended'
					}
					strategyColumnTitle={
						activeTab === 'implemented' ? 'Implemented strategy' : 'Recommended strategy'
					}
					locationOptions={locationOptions}
					selectedLocations={selectedLocations}
					onLocationsChange={onLocationsChange}
					locationSummary={locationSummary}
					showExport={hasExportData}
					onExport={onExport}
				/>
			) : (
				<NoRecords />
			)}
		</div>
	);
};

export default DecisionLogView;
